import React, { useEffect } from 'react';
import { Input, Select } from 'syngenta-digital-cropwise-react-ui-kit';
import moment, { Moment } from 'moment';
import { useFormikContext } from 'formik';

import {
  FormValues,
  initialValues
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import generateDefaultApplicationId from 'screens/Applications&WorkOrders/StepsLayout/Helpers/generateDefaultApplicationId';
import { getInitials } from 'helpers/textHelpers';
import { useAuth } from 'utilities/base-auth';
import LayoutStrings from 'screens/Applications&WorkOrders/StepsLayout/Constants/layoutStrings';
import {
  timingEventOptions,
  workTypeOptions
} from 'screens/Applications&WorkOrders/StepsLayout/Constants/selectOptions';
import ChevronUp from 'assets/chevron_up.svg?react';
import ChevronDown from 'assets/chevron_down.svg?react';
import { COL_SPAN_ONE_THIRD } from 'components/FormGrid/constants';
import FormGrid, { FormRowConfig } from 'components/FormGrid';
import getDropdownParentWithoutDetaching from 'screens/Applications&WorkOrders/StepsLayout/Helpers/getDropdownParentWithoutDetaching';
import { EquipmentItem } from 'components/EquipmentDropdown/types';
import {
  OperationType,
  useAgOperationUtility
} from 'screens/Applications&WorkOrders/StepsLayout/Context/AgOperationUtilityProvider';
import { buildWorkOrderFields, buildApplicationFields } from './fieldBuilders';

interface DropdownState {
  timingEvent: boolean;
  workType: boolean;
  authorizedBy: boolean;
  applicationMethod: boolean;
  equipment: boolean;
}

// Same constants as original for consistency
const FULL_WIDTH_STYLE = { width: '100%' };

// Helper: Calculate duration in minutes from hours and minutes
const calculateDurationInMinutes = (
  hours: number | undefined,
  minutes: number | undefined
): number => {
  if (hours === undefined || minutes === undefined) {
    return 0;
  }
  return hours * 60 + minutes;
};

// Helper: Calculate other time field based on duration
const calculateOtherTime = (
  field: 'startDateTime' | 'endDateTime',
  value: Moment | null | undefined,
  durationInMinutes: number
): Moment | null => {
  if (!value) return null;

  if (field === 'startDateTime') {
    return moment(value).add(durationInMinutes, 'minutes');
  }
  return moment(value).subtract(durationInMinutes, 'minutes');
};

// Helper: Normalize equipment items to array
const normalizeEquipmentItems = (
  equipmentList: EquipmentItem[] | EquipmentItem | null | undefined
): EquipmentItem[] => {
  if (!equipmentList) return [];

  const equipmentArray = Array.isArray(equipmentList)
    ? equipmentList
    : [equipmentList];

  return equipmentArray.filter(
    item => typeof item === 'object' && item !== null
  );
};

interface DetailsSectionProps {
  excludeFields?: string[];
}

// Helper: Custom hook for dropdown state management
const useDropdownState = () => {
  const [openDropdowns, setOpenDropdowns] = React.useState<DropdownState>({
    timingEvent: false,
    workType: false,
    authorizedBy: false,
    applicationMethod: false,
    equipment: false
  });

  const showChevron = (key: keyof DropdownState): React.ReactNode => {
    return openDropdowns[key] ? (
      <ChevronUp width={18} height={18} />
    ) : (
      <ChevronDown width={18} height={18} />
    );
  };

  const handleDropdownVisibleChange = (
    open: boolean,
    field: keyof DropdownState
  ): void => {
    setOpenDropdowns(prev => ({
      ...prev,
      [field]: open
    }));
  };

  return { openDropdowns, showChevron, handleDropdownVisibleChange };
};

const DetailsSection: React.FC<DetailsSectionProps> = ({
  excludeFields = []
}) => {
  const {
    operationType,
    propagateApplicationMethodToProducts
  } = useAgOperationUtility();
  const { user } = useAuth();
  const {
    values,
    errors,
    touched,
    setFieldTouched,
    validateField,
    handleChange,
    handleBlur,
    setFieldValue
  } = useFormikContext<FormValues>();

  const { showChevron, handleDropdownVisibleChange } = useDropdownState();

  const today = moment();
  const defaultStartTime = moment().set({
    hour: 8,
    minute: 0,
    second: 0,
    millisecond: 0
  });
  const defaultEndTime = moment().set({
    hour: 10,
    minute: 0,
    second: 0,
    millisecond: 0
  });

  const preventDecimalInput = (
    e: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    if (e.key === '.') {
      e.preventDefault();
    }
  };

  const handleTimeChange = (
    field: 'startDateTime' | 'endDateTime',
    value: Moment | null | undefined
  ): void => {
    if (field === 'startDateTime') {
      // Start time changed: keep duration fixed, update end time
      const durationInMinutes = calculateDurationInMinutes(
        values.durationHours,
        values.durationMinutes
      );
      const endTime = calculateOtherTime(
        'startDateTime',
        value,
        durationInMinutes
      );
      setFieldValue('startDateTime', value);
      setFieldValue('endDateTime', endTime);
    } else {
      // End time changed: update duration, keep start time fixed
      const startTime = values.startDateTime;

      if (!value || !startTime) {
        setFieldValue('endDateTime', value);
        return;
      }

      // Check if end time is before start time
      if (value.isBefore(startTime)) {
        // Keep duration fixed, update start time backwards
        const durationInMinutes = calculateDurationInMinutes(
          values.durationHours,
          values.durationMinutes
        );
        const newStartTime = moment(value).subtract(
          durationInMinutes,
          'minutes'
        );
        setFieldValue('endDateTime', value);
        setFieldValue('startDateTime', newStartTime);
      } else {
        // Calculate new duration from start to end
        const durationInMinutes = value
          .clone()
          .millisecond(0)
          .diff(startTime.clone().millisecond(0), 'minutes');
        const newHours = Math.floor(durationInMinutes / 60);
        const newMinutes = durationInMinutes % 60;

        setFieldValue('endDateTime', value);
        setFieldValue('durationHours', newHours);
        setFieldValue('durationMinutes', newMinutes);
      }
    }
  };

  const handleEquipmentChange = (
    value: EquipmentItem | EquipmentItem[] | null
  ): void => {
    if (Array.isArray(value)) {
      const equipmentItems = normalizeEquipmentItems(value);
      setFieldValue(
        'equipments',
        equipmentItems.length ? equipmentItems : null
      );
      return;
    }

    const existingEquipmentList = normalizeEquipmentItems(values.equipments);
    setFieldValue('equipments', [...existingEquipmentList, value]);
  };

  const getEquipmentValue = (): EquipmentItem[] | null => {
    const normalized = normalizeEquipmentItems(values.equipments);
    return normalized.length ? normalized : null;
  };

  // Initialize application ID on mount
  useEffect(() => {
    if (!values.applicationId) {
      setFieldValue(
        'applicationId',
        generateDefaultApplicationId(getInitials(user?.name ?? ''))
      );
      setFieldTouched('applicationId', true);
      validateField('applicationId');
    }
    const input = document.querySelector<HTMLInputElement>(
      'input[name="applicationId"]'
    );
    if (input) input.focus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Validate applicationId when it changes
  useEffect(() => {
    setFieldTouched('applicationId', true);
    validateField('applicationId');
  }, [values.applicationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Update end time when duration changes
  useEffect(() => {
    handleTimeChange(
      'startDateTime',
      values.startDateTime ?? initialValues.startDateTime
    );
  }, [values.durationHours, values.durationMinutes]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderSelectOptions = (options: { value: string; label: string }[]) =>
    options.map(opt => (
      <Select.Option
        key={opt.value}
        value={opt.value}
        data-testid={`${opt.label}-option`}
      >
        {opt.label}
      </Select.Option>
    ));

  // Build field configuration props
  const fieldBuilderProps = {
    values,
    errors,
    touched,
    today,
    defaultStartTime,
    defaultEndTime,
    showChevron,
    handleTimeChange,
    handleBlur,
    setFieldValue,
    handleDropdownVisibleChange,
    handleEquipmentChange,
    getEquipmentValue,
    renderSelectOptions,
    preventDecimalInput,
    onApplicationMethodChange: propagateApplicationMethodToProducts
  };

  // Get dynamic fields based on operation type
  const dynamicFields =
    operationType === OperationType.WORK_ORDER
      ? buildWorkOrderFields(fieldBuilderProps)
      : buildApplicationFields(fieldBuilderProps);

  // Form configuration using FormGrid pattern
  const formRows: FormRowConfig[] = [
    {
      id: 'basic-info-row',
      fields: [
        {
          id: 'applicationId',
          label: LayoutStrings.fields[operationType!].id,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: touched.applicationId ? errors.applicationId : undefined,
          render: () => (
            <Input
              data-testid="application-id-input"
              name="applicationId"
              placeholder={LayoutStrings.placeholders[operationType!].id}
              className="w-full no-margin truncate whitespace-nowrap overflow-hidden"
              status={errors.applicationId ? 'error' : undefined}
              prefixCls="syt-antd-input"
              value={values.applicationId}
              onChange={handleChange}
              onBlur={handleBlur}
              size="middle"
            />
          )
        },
        {
          id: 'workType',
          label: LayoutStrings.fields.workType,
          colSpan: COL_SPAN_ONE_THIRD,
          required: true,
          error: touched.workType ? errors.workType : undefined,
          render: () => (
            <Select
              data-testid="work-type-select"
              value={values.workType ?? 'general'}
              style={FULL_WIDTH_STYLE}
              className="custom-select"
              showSearch
              onChange={value => setFieldValue('workType', value)}
              onBlur={() => handleBlur({ target: { name: 'workType' } })}
              suffixIcon={showChevron('workType')}
              onDropdownVisibleChange={(open: boolean) =>
                handleDropdownVisibleChange(open, 'workType')
              }
              getPopupContainer={getDropdownParentWithoutDetaching}
            >
              {renderSelectOptions(workTypeOptions)}
            </Select>
          )
        },
        {
          id: 'timingEvent',
          label: LayoutStrings.fields.timingEvent,
          colSpan: COL_SPAN_ONE_THIRD,
          error: touched.timingEvent ? errors.timingEvent : undefined,
          render: () => (
            <Select
              data-testid="timing-event-select"
              style={FULL_WIDTH_STYLE}
              showSearch
              value={values.timingEvent}
              onChange={(value: string) => setFieldValue('timingEvent', value)}
              onBlur={() => handleBlur({ target: { name: 'timingEvent' } })}
              allowClear={!!values.timingEvent}
              className="custom-select"
              showArrow
              suffixIcon={showChevron('timingEvent')}
              onDropdownVisibleChange={(open: boolean) =>
                handleDropdownVisibleChange(open, 'timingEvent')
              }
              getPopupContainer={getDropdownParentWithoutDetaching}
            >
              {renderSelectOptions(timingEventOptions)}
            </Select>
          )
        }
      ]
    },
    ...dynamicFields
  ];

  return (
    <FormGrid
      rows={formRows}
      excludeFields={excludeFields}
      className="flex flex-col gap-1 -mb-5"
    />
  );
};

export default DetailsSection;
